import AttachmentTable from './AttachmentsTable';
import { SideSheet, SideSheetHeader } from '../../../layouts';
import { formatDayJsDate } from '../../../utils/dateTimeUtils';
import { convertDateToDayJs } from '../../../utils/dateTimeUtils';
import dayjs from 'dayjs';

interface AccommodationLineItemDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  lineItemData: any;
  attachments: any[];
  travelRequestId: number;
  formConfig: any;
  headerChildren?: React.ReactNode;
}

function GridDataPoint({
  config,
}: {
  config: { label: string; value: React.ReactNode };
}) {
  const { label, value } = config;
  return (
    <div className="flex flex-col gap-y-1">
      <div className="font-semibold text-text-200 text-xs">{label}</div>
      <div className="font-normal text-text-400 text-xs">{value}</div>
    </div>
  );
}

const AccommodationLineItemDetails = ({
  isOpen,
  onClose,
  lineItemData,
  attachments,
  travelRequestId,
  formConfig,
  headerChildren,
}: AccommodationLineItemDetailsProps) => {
  if (!isOpen) return null;

  const fields = formConfig?.accommodationForm?.fields;

  return (
    <SideSheet
      isOpen={isOpen}
              addOnClass="w-full max-w-none md:!w-[50vw]"
      onClose={onClose}
      closeOnClickOutside={true}
      contentChildren={
        <div className="flex flex-col gap-4 p-3">
          {lineItemData && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {fields?.accommodationType?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.accommodationType?.label ||
                        fields?.accommodationType?.defaultLabel ||
                        'Accommodation Type',
                      value:
                        lineItemData.accommodationType?.label ||
                        lineItemData.travelAccommodation?.name ||
                        '-',
                    }}
                  />
                )}
                {fields?.checkInDate?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.checkInDate?.label ||
                        fields?.checkInDate?.defaultLabel ||
                        'Check-in Date',
                      value: lineItemData.checkInDate
                        ? formatDayJsDate(
                            convertDateToDayJs(lineItemData.checkInDate),
                          )
                        : '-',
                    }}
                  />
                )}
                {fields?.checkInTime?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.checkInTime?.label ||
                        fields?.checkInTime?.defaultLabel ||
                        'Check-in Time',
                      value: lineItemData.checkInTime
                        ? dayjs(lineItemData.checkInTime, 'HH:mm').format(
                            'hh:mm A',
                          )
                        : '-',
                    }}
                  />
                )}
                {fields?.checkOutDate?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.checkOutDate?.label ||
                        fields?.checkOutDate?.defaultLabel ||
                        'Check-out Date',
                      value: lineItemData.checkOutDate
                        ? formatDayJsDate(
                            convertDateToDayJs(lineItemData.checkOutDate),
                          )
                        : '-',
                    }}
                  />
                )}
                {fields?.checkOutTime?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.checkOutTime?.label ||
                        fields?.checkOutTime?.defaultLabel ||
                        'Check-out Time',
                      value: lineItemData.checkOutTime
                        ? dayjs(lineItemData.checkOutTime, 'HH:mm').format(
                            'hh:mm A',
                          )
                        : '-',
                    }}
                  />
                )}
                {fields?.country?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.country?.label ||
                        fields?.country?.defaultLabel ||
                        'Country',
                      value:
                        lineItemData.country?.label ||
                        lineItemData.travelToCountry?.LongDescription ||
                        '-',
                    }}
                  />
                )}
                {fields?.city?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.city?.label ||
                        fields?.city?.defaultLabel ||
                        'City',
                      value:
                        lineItemData.city?.label ||
                        lineItemData.travelToCity?.ShortDescription ||
                        '-',
                    }}
                  />
                )}
                {fields?.remarks?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.remarks?.label ||
                        fields?.remarks?.defaultLabel ||
                        'Remarks',
                      value: lineItemData.remarks || '-',
                    }}
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <h2 className="text-md">Attachments</h2>
            <AttachmentTable
              attachments={attachments || []}
              travelRequestId={travelRequestId}
            />
          </div>
        </div>
      }
      headerChildren={
        <div className="border-b border-gray-100">
          {headerChildren || (
            <SideSheetHeader
              title="Accommodation Details"
              onCloseClick={onClose}
            />
          )}
        </div>
      }
      footerChildren={<></>}
    />
  );
};

export default AccommodationLineItemDetails;
